import { useState, useEffect } from "react";
import {
  X,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  Maximize2,
} from "lucide-react";
import { Modal } from "./Modal";
import { IconButton } from "./IconButton";
import { Button } from "./Button";

export interface ImageViewerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  src?: string | null;
  alt?: string;
  title?: string;
}

/**
 * ImageViewerModal - Fullscreen / Lightbox image preview modal with zoom, rotate, and download controls.
 */
export function ImageViewerModal({
  open,
  onOpenChange,
  src,
  alt = "Hình ảnh",
  title,
}: ImageViewerModalProps) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Reset transform when modal opens or image changes
  useEffect(() => {
    if (open) {
      setScale(1);
      setRotation(0);
    }
  }, [open, src]);

  const handleZoomIn = () => setScale((s) => Math.min(s + 0.25, 3));
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5));
  const handleRotate = () => setRotation((r) => (r + 90) % 360);
  const handleReset = () => {
    setScale(1);
    setRotation(0);
  };

  const handleDownload = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = alt.replace(/\s+/g, "_") || "image_download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!src) return null;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title || alt}
      size="xl"
      height="tall"
      preventOutsideClose={false}
      bodyClassName="p-0 overflow-hidden flex flex-col flex-1"
    >
      <div className="relative flex flex-col h-[70vh] max-h-[calc(92dvh-5rem)] w-full overflow-hidden select-none bg-bg/95">
        {/* Viewport Canvas */}
        <div className="relative flex-1 min-h-0 w-full overflow-hidden flex items-center justify-center p-6 pb-16">
          <div
            className="transition-transform duration-150 ease-out flex items-center justify-center max-w-full max-h-full"
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
            }}
          >
            <img
              src={src}
              alt={alt}
              className="max-h-[calc(70vh-7rem)] max-w-full object-contain rounded-control shadow-md pointer-events-none select-none"
            />
          </div>

          {/* Fixed Floating Bottom Toolbar */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 rounded-full border border-border/90 bg-surface/95 px-3.5 py-1.5 shadow-lg backdrop-blur-md select-none">
            <span className="text-caption font-mono text-text-muted pr-2 border-r border-border/70">
              {Math.round(scale * 100)}% · {rotation}°
            </span>

            <div className="flex items-center gap-0.5">
              <IconButton
                icon={<ZoomOut className="size-4" />}
                size="sm"
                variant="ghost"
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                label="Thu nhỏ"
              />
              <IconButton
                icon={<ZoomIn className="size-4" />}
                size="sm"
                variant="ghost"
                onClick={handleZoomIn}
                disabled={scale >= 3}
                label="Phóng to"
              />
              <IconButton
                icon={<RotateCw className="size-4" />}
                size="sm"
                variant="ghost"
                onClick={handleRotate}
                label="Xoay 90 độ"
              />
              <IconButton
                icon={<Download className="size-4" />}
                size="sm"
                variant="ghost"
                onClick={handleDownload}
                label="Tải ảnh về"
              />
              <div className="mx-1 h-3.5 w-px bg-border/70" />
              <Button
                size="sm"
                variant="ghost"
                onClick={handleReset}
                className="text-caption px-2 h-7"
              >
                Mặc định
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
